import { computed, nextTick, onUnmounted, ref, watch, type Ref } from "vue";
import { cardAtPoint, reorderCards } from "./cardOrder";

type Card = { id: string; name: string };
export function useCardReorder(options: {
  cards: () => Card[];
  enabled: () => boolean;
  grid: Ref<HTMLElement | undefined>;
  save: (ids: string[]) => Promise<void>;
  onError: (error: unknown) => void;
}) {
  const liftedId = ref<string | null>(null),
    targetId = ref<string | null>(null);
  const dragging = ref(false),
    saving = ref(false),
    status = ref("");
  const position = ref({ x: 0, y: 0 });
  const name = computed(
    () => options.cards().find((c) => c.id === liftedId.value)?.name,
  );
  const targetIndex = computed(() =>
    options.cards().findIndex((c) => c.id === targetId.value),
  );
  const sourceIndex = computed(() =>
    options.cards().findIndex((c) => c.id === liftedId.value),
  );
  let pointer:
    | {
        id: number;
        card: string;
        startX: number;
        startY: number;
        x: number;
        y: number;
        handle: HTMLElement;
      }
    | undefined;
  let frame = 0,
    suppressClickUntil = 0;
  const handles = () =>
    options.grid.value?.querySelectorAll<HTMLButtonElement>(
      "[data-drag-handle]",
    );
  async function focus(id: string) {
    await nextTick();
    Array.from(handles() ?? [])
      .find((el) => el.dataset.dragHandle === id)
      ?.focus({ preventScroll: true });
  }
  function announce() {
    status.value = `${name.value}. Placering ${targetIndex.value + 1} af ${options.cards().length}.`;
  }
  function releasePointer() {
    cancelAnimationFrame(frame);
    const current = pointer;
    pointer = undefined;
    if (current?.handle.hasPointerCapture(current.id))
      current.handle.releasePointerCapture(current.id);
    window.removeEventListener("pointermove", pointerMove);
    window.removeEventListener("pointerup", pointerUp);
    window.removeEventListener("pointercancel", cancel);
  }
  function reset() {
    releasePointer();
    dragging.value = false;
    liftedId.value = targetId.value = null;
    window.removeEventListener("blur", cancel);
    window.removeEventListener("keydown", escape);
  }
  function cancel() {
    if (liftedId.value) status.value = "Flytning annulleret.";
    reset();
  }
  function escape(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
    }
  }
  function lift(id: string) {
    liftedId.value = targetId.value = id;
    window.addEventListener("blur", cancel);
    window.addEventListener("keydown", escape);
    announce();
  }
  async function commit() {
    const source = liftedId.value,
      target = targetId.value;
    const label = name.value;
    const index = targetIndex.value;
    const ids = options.cards().map((c) => c.id);
    reset();
    if (!source || !target || source === target || !options.enabled()) return;
    saving.value = true;
    try {
      await options.save(reorderCards(ids, source, target));
      status.value = `${label} er flyttet til placering ${index + 1}.`;
    } catch (error) {
      status.value = "Rækkefølgen kunne ikke gemmes.";
      options.onError(error);
    } finally {
      saving.value = false;
      void focus(source);
    }
  }
  function locate() {
    if (!pointer) return;
    const cards = Array.from(
      options.grid.value?.querySelectorAll<HTMLElement>("[data-card-id]") ?? [],
    ).map((el) => ({ id: el.dataset.cardId!, ...rect(el) }));
    const next = cardAtPoint(cards, pointer.x, pointer.y);
    if (next !== targetId.value) {
      targetId.value = next;
      if (next) announce();
    }
    position.value = {
      x: Math.max(12, Math.min(pointer.x + 16, window.innerWidth - 272)),
      y: Math.max(12, Math.min(pointer.y + 16, window.innerHeight - 72)),
    };
  }
  function rect(el: HTMLElement) {
    const { left, right, top, bottom } = el.getBoundingClientRect();
    return { left, right, top, bottom };
  }
  function scroll() {
    if (!pointer || !dragging.value) return;
    const edge = 72;
    const speed =
      pointer.y < edge
        ? -Math.min(16, (edge - pointer.y) / 4)
        : pointer.y > window.innerHeight - edge
          ? Math.min(16, (pointer.y - window.innerHeight + edge) / 4)
          : 0;
    if (speed) {
      window.scrollBy(0, speed);
      locate();
    }
    frame = requestAnimationFrame(scroll);
  }
  function pointerDown(event: PointerEvent, id: string) {
    if (
      !options.enabled() ||
      saving.value ||
      event.button !== 0 ||
      !event.isPrimary
    )
      return;
    releasePointer();
    const handle = event.currentTarget as HTMLElement;
    pointer = {
      id: event.pointerId,
      card: id,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      handle,
    };
    handle.setPointerCapture(event.pointerId);
    window.addEventListener("pointermove", pointerMove, { passive: false });
    window.addEventListener("pointerup", pointerUp);
    window.addEventListener("pointercancel", cancel);
  }
  function pointerMove(event: PointerEvent) {
    if (!pointer || pointer.id !== event.pointerId) return;
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    if (!dragging.value) {
      if (
        Math.hypot(pointer.x - pointer.startX, pointer.y - pointer.startY) < 6
      )
        return;
      lift(pointer.card);
      dragging.value = true;
      frame = requestAnimationFrame(scroll);
    }
    event.preventDefault();
    locate();
  }
  function pointerUp(event: PointerEvent) {
    if (!pointer || pointer.id !== event.pointerId) return;
    if (dragging.value) {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      locate();
      suppressClickUntil = performance.now() + 250;
      void commit();
    } else releasePointer();
  }
  function click(id: string) {
    if (
      !options.enabled() ||
      saving.value ||
      performance.now() < suppressClickUntil
    )
      return;
    if (!liftedId.value) lift(id);
    else {
      if (id !== liftedId.value) targetId.value = id;
      void commit();
    }
  }
  function keydown(event: KeyboardEvent, id: string) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      click(id);
      return;
    }
    if (!liftedId.value || dragging.value) return;
    if (event.key === "Tab") return cancel();
    const keys = [
      "ArrowLeft",
      "ArrowUp",
      "ArrowRight",
      "ArrowDown",
      "Home",
      "End",
    ];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const cards = options.cards();
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? cards.length - 1
          : targetIndex.value +
            (["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 1);
    targetId.value =
      cards[Math.max(0, Math.min(next, cards.length - 1))]?.id ?? null;
    announce();
  }
  watch(
    () =>
      options
        .cards()
        .map((c) => c.id)
        .join(","),
    cancel,
  );
  watch(options.enabled, (enabled) => {
    if (!enabled) cancel();
  });
  onUnmounted(reset);
  return {
    liftedId,
    targetId,
    sourceIndex,
    targetIndex,
    dragging,
    saving,
    status,
    position,
    name,
    pointerDown,
    click,
    keydown,
  };
}
